import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { useIsMobile } from '@src/hooks'
import type SwiperCore from 'swiper'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import { Container, HeaderText, ViewAllLink, NavigationButton } from './CarouselHeader.styled'

interface Props {
  text: string
  swiperRef?: React.MutableRefObject<SwiperCore | null>
  icon: React.ReactNode
  linkHref?: string
  isControlsVisible?: boolean
}

const CarouselHeader = ({ text, swiperRef, icon, linkHref, isControlsVisible = true }: Props) => {
  const isMobile = useIsMobile('sm')
  const location = useLocation()
  const { t } = useTranslation()
  const currentPath = location.pathname

  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  useEffect(() => {
    if (swiperRef?.current) {
      const swiper = swiperRef.current

      // Set initial states
      setIsBeginning(swiper.isBeginning)
      setIsEnd(swiper.isEnd)

      // Add event listeners for slide changes
      swiper.on('slideChange', () => {
        setIsBeginning(swiper.isBeginning)
        setIsEnd(swiper.isEnd)
      })

      // Add event listeners for resize (which can change isBeginning/isEnd)
      swiper.on('resize', () => {
        setIsBeginning(swiper.isBeginning)
        setIsEnd(swiper.isEnd)
      })

      // Cleanup
      return () => {
        swiper.off('slideChange')
        swiper.off('resize')
      }
    }
  }, [swiperRef?.current])

  const handlePrevClick = () => {
    if (!isBeginning && swiperRef?.current) {
      swiperRef.current.slidePrev()
    }
  }

  const handleNextClick = () => {
    if (!isEnd && swiperRef?.current) {
      swiperRef.current.slideNext()
    }
  }

  return (
    <Container>
      {icon}
      <HeaderText>{text}</HeaderText>
      {!!linkHref && linkHref !== currentPath && (
        <ViewAllLink to={linkHref}>{t('carouselHeading.viewAll', 'View all')}</ViewAllLink>
      )}
      {swiperRef && isControlsVisible && (
        <>
          <NavigationButton $isMobile={isMobile} disabled={isBeginning} onClick={handlePrevClick}>
            <FontAwesomeIcon icon={faChevronLeft} width={20} size="xs" />
          </NavigationButton>
          <NavigationButton $isMobile={isMobile} disabled={isEnd} onClick={handleNextClick}>
            <FontAwesomeIcon icon={faChevronRight} width={20} size="xs" />
          </NavigationButton>
        </>
      )}
    </Container>
  )
}

export default CarouselHeader
